<template>
  <div id="chartdiv" ref="chartdiv"></div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { mapState } from "vuex";
import _ from "lodash";
import { REFINERY_ID, TANKER_TYPE } from "@/constant";

export default {
  data() {
    return {
      categoryAxis: null,
      color: "",
    };
  },
  mounted() {
    const mapped = this.createAllData();
    this.init(mapped);
  },
  computed: {
    ...mapState({
      radarName: (state) => state.radarData.refineryRadarName,
      item: (state) => state.vesselTrip.data,
    }),
  },
  watch: {
    radarName(val, oldval) {
      if (!_.isEqual(val, oldval)) {
        this.changeData(val);
      }
    },
    item(val, oldval) {
      if (!_.isEqual(val, oldval)) {
        const mapped = this.createAllData();
        // console.log(mapped);
        this.init(mapped);
      }
    },
  },
  methods: {
    createRange(name, continentData, index) {
      const sliceData = [];
      for (let i = 0; i < continentData.length; i++) {
        sliceData[i] = _.values(continentData[i]);
      }
      let axisRange = this.categoryAxis.axisRanges.create();
      axisRange.axisFill.interactionsEnabled = true;
      axisRange.axisFill.rname = name;
      axisRange.text = name;
      // first vessel
      axisRange.category = sliceData[0][1];

      // last vessel
      axisRange.endCategory = sliceData[sliceData.length - 1][1];

      // every 3rd color for a bigger contrast
      axisRange.axisFill.fill = this.colorSet.getIndex(index * 3);
      axisRange.grid.disabled = true;
      axisRange.label.interactionsEnabled = false;
      axisRange.label.bent = true;

      let axisFill = axisRange.axisFill;
      axisFill.innerRadius = -0.001; // almost the same as 100%, we set it in pixels as later we animate this property to some pixel value
      axisFill.radius = -20; // negative radius means it is calculated from max radius
      axisFill.disabled = false; // as regular fills are disabled, we need to enable this one
      axisFill.fillOpacity = 0.5;
      axisFill.togglable = true;

      axisFill.showSystemTooltip = true;
      axisFill.readerTitle = name;
      axisFill.cursorOverStyle = am4core.MouseCursorStyle.pointer;

      const vm = this;

      axisFill.events.on("hit", function(event) {
        let dataItem = event.target.dataItem;
        let refineryName = event.target.rname;
        let type = TANKER_TYPE.TANKER;
        vm.$store.dispatch("setTankerName", refineryName);
        vm.$store.dispatch("setType", type);

        if (!event.target.isActive) {
          vm.categoryAxis.zoom({ start: 0, end: 1 });
        } else {
          vm.categoryAxis.zoomToCategories(
            dataItem.category,
            dataItem.endCategory
          );
        }
      });

      // hover state
      let hoverState = axisFill.states.create("hover");
      hoverState.properties.innerRadius = -10;
      hoverState.properties.radius = -25;

      let axisLabel = axisRange.label;
      axisLabel.location = 0.5;
      axisLabel.fill = am4core.color("#ffffff");
      axisLabel.radius = 3;
      axisLabel.relativeRotation = 0;
    },
    generateRadarData(Item) {
      let data = [];
      let i = 0;
      for (let continent in Item) {
        let continentData = Item[continent];
        _.forEach(continentData, function(vessel) {
          let rawDataItem = { vesselid: vessel.id };
          rawDataItem["vessel_name"] = vessel.name;
          rawDataItem["value"] = vessel.value;
          rawDataItem["type"] = vessel.type;
          rawDataItem["name"] = vessel.vessel_name;
          data.push(rawDataItem);
        });
        this.createRange(continent, continentData, i);
        i++;
      }
      // console.log(data);
      return data;
    },

    init(item) {
      am4core.useTheme(am4themes_animated);

      let colorSet = new am4core.ColorSet();
      this.colorSet = colorSet;
      let chart = am4core.create(this.$refs.chartdiv, am4charts.RadarChart);
      // chart.numberFormatter.numberFormat = "#as" 
      chart.align = "center";
      chart.innerRadius = am4core.percent(10);

      let zoomOutButton = chart.zoomOutButton;
      zoomOutButton.dx = 0;
      zoomOutButton.dy = 0;
      zoomOutButton.marginBottom = 15;
      zoomOutButton.parent = chart.rightAxesContainer;

      chart.rightAxesContainer.layout = "vertical";

      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.dataFields.category = "vessel_name";

      let categoryAxisRenderer = categoryAxis.renderer;
      let categoryAxisLabel = categoryAxisRenderer.labels.template;
      categoryAxisLabel.location = 0.5;
      categoryAxisLabel.radius = 28;
      categoryAxisLabel.relativeRotation = 90;

      let labelTemplate = categoryAxis.renderer.labels.template;
      labelTemplate.disabled = true;

      categoryAxisRenderer.fontSize = 11;
      categoryAxisRenderer.minGridDistance = 10;
      categoryAxisRenderer.grid.template.radius = -25;
      categoryAxisRenderer.grid.template.strokeOpacity = 0.05;
      categoryAxisRenderer.grid.template.interactionsEnabled = false;

      categoryAxisRenderer.ticks.template.disabled = true;
      categoryAxisRenderer.axisFills.template.disabled = true;
      categoryAxisRenderer.line.disabled = true;

      categoryAxisRenderer.tooltipLocation = 0.5;
      categoryAxis.tooltip.defaultState.properties.opacity = 0;
      this.categoryAxis = categoryAxis;
      // value axis
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 1;
      // valueAxis.max = 10000000;
      valueAxis.strictMinMax = true;
      valueAxis.tooltip.defaultState.properties.opacity = 0;
      valueAxis.tooltip.animationDuration = 0;
      valueAxis.cursorTooltipEnabled = true;
      valueAxis.zIndex = 10;
      valueAxis.tooltip.disabled = true;
      // valueAxis.renderer.grid.template.disabled = true;
      valueAxis.renderer.labels.template.disabled = true;

      let valueAxisRenderer = valueAxis.renderer;
      valueAxisRenderer.axisFills.template.disabled = true;
      valueAxisRenderer.ticks.template.disabled = true;
      valueAxisRenderer.minGridDistance = 20;
      valueAxisRenderer.grid.template.strokeOpacity = 0.2;

      let vm = this;

      // series
      let series = chart.series.push(new am4charts.RadarColumnSeries());
      series.columns.template.width = am4core.percent(90);
      series.columns.template.strokeOpacity = 0;
      series.dataFields.valueY = "value";
      series.dataFields.categoryX = "vessel_name";
      series.tooltipText = "{categoryX}:{valueY.value} ลิตร";
      series.columns.template.radarColumn.cornerRadius = 10;
      series.columns.template.radarColumn.innerCornerRadius = 10;

      series.columns.template.events.on("hit", function(event) {
        let vesselName = event.target.dataItem._dataContext.vessel_name;
        let type = event.target.dataItem._dataContext.type;
        let name = event.target.dataItem._dataContext.name;
        // console.log(type);
        if (type == TANKER_TYPE.TANKER) {
          vm.$store.dispatch("setType", type);
          vm.$store.dispatch("setTankerName", name);
        }
      });

      series.columns.template.adapter.add("fill", (fill, target) => {
        return chart.colors.getIndex(target.dataItem.index);
      });

      // cursor
      let cursor = new am4charts.RadarCursor();
      chart.cursor = cursor;
      cursor.behavior = "zoomX";

      cursor.xAxis = categoryAxis;
      cursor.innerRadius = am4core.percent(40);
      cursor.lineY.disabled = true;

      cursor.lineX.fillOpacity = 0.2;
      cursor.lineX.fill = am4core.color("#000000");
      cursor.lineX.strokeOpacity = 0;
      cursor.fullWidthLineX = true;

      chart.data = this.generateRadarData(item);
    },
    changeData(val) {
      this.$store.dispatch("setRadarName", val);
    },
    createAllData() {
      let n = 0;
      const radar = _.chain(this.item)
        .groupBy("station_id")
        .map((o, i) => {
          n = 0;
          const name = _.findKey(REFINERY_ID, (val) => val == i) || "OTHER";
          const children = _.chain(o)
            .groupBy("vessel_id")
            .map((a) => {
              n++;
              return {
                id: n,
                name:
                  _.get(_.find(a, "vessel.name"), "vessel.name") +
                  "_" +
                  _.get(_.find(a, "station.code"), "station.code"),
                vessel_name: _.get(_.find(a, "vessel.name"), "vessel.name"),
                value: _.reduce(
                  a,
                  (sum, a) => {
                    let volumes = _.get(a, "volumes").map((a) => {
                      return Number(a);
                    });
                    return sum + _.sum(volumes);
                  },
                  0
                ),
                type: "tanker",
              };
            })
            .value();

          return {
            [name]: children,
          };
        })
        .value();
      const mapped = {};
      _.forEach(radar, (radar) => {
        _.assign(mapped, radar);
      });
      return mapped;
    },
  },
};
</script>

<style>
#chartdiv {
  width: 100%;
  height: 400px;
}
</style>
